import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, MessageCircle, Ticket, PartyPopper } from "lucide-react";
import { WHATSAPP_URL, INTEREST_HREF, INTEREST_ANCHOR } from "../lib/constants";
import { trackNotifyClick, trackTicketClick, trackWhatsAppClick } from "../lib/track";
import { useUpcomingShows, useTodayIST, compactCountdown } from "../lib/shows";
import { ROUTES } from "../lib/seo";
import { HOST_FORM_ANCHOR } from "../pages/HostAntara";

/**
 * Mobile action bar.
 *
 * While a Bhaav Sangam show is ahead it carries one button per city that goes
 * straight to that city's BookMyShow page, so booking is a single tap from
 * anywhere. It shows from the first screen, except while the home page's own
 * booking strip (data-testid="hero-book-strip") is in view: two sets of the
 * same buttons on one screen is noise, not help.
 *
 * Once both dates have passed it carries Host Antara (the site's first ask)
 * and the interest list, shown after the visitor has scrolled a little. On
 * the Host Antara page itself the hosting button steps aside for the city
 * one. The desktop floating "book" button was removed with ticketing rather
 * than repointed.
 */
export function StickyMobileBar() {
  const shows = useUpcomingShows();
  const live = shows.length > 0;
  const today = useTodayIST();
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const onHostPage = pathname.replace(/\/$/, "") === ROUTES.host.path.replace(/\/$/, "");

  useEffect(() => {
    if (live) {
      const strip = document.querySelector('[data-testid="hero-book-strip"]');
      if (!strip || typeof IntersectionObserver === "undefined") {
        setShow(true);
        return undefined;
      }
      const io = new IntersectionObserver(([e]) => setShow(!e.isIntersecting));
      io.observe(strip);
      return () => io.disconnect();
    }
    // Off while the form itself is on screen: a button pointing at the form,
    // laid over the form, only gets in the way of filling it in.
    let formInView = false;
    const handler = () => setShow(window.scrollY > 500 && !formInView);
    const form = document.getElementById(onHostPage ? HOST_FORM_ANCHOR : INTEREST_ANCHOR);
    const io = form && typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver(([e]) => { formInView = e.isIntersecting; handler(); })
      : null;
    if (io) io.observe(form);
    handler();
    window.addEventListener("scroll", handler);
    return () => {
      window.removeEventListener("scroll", handler);
      if (io) io.disconnect();
    };
  }, [live, pathname, onHostPage]);

  if (!show) return null;
  return (
    <div className="sticky-mobile md:hidden" data-testid="sticky-mobile-bar">
      {live ? (
        shows.map((s) => (
          <a
            key={s.id}
            href={s.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackTicketClick(s, "sticky")}
            className="flex-1 min-w-0 btn-primary flex-col"
            style={{ padding: "8px 8px", gap: 0, borderRadius: 14, fontSize: 12, lineHeight: 1.25, textTransform: "none", letterSpacing: "0.02em" }}
            data-testid={`mobile-book-${s.id}`}
          >
            <span className="flex items-center gap-1.5 font-semibold" style={{ fontSize: 13 }}>
              <Ticket size={13} /> {s.city}
            </span>
            <span className="truncate" style={{ fontSize: 11, opacity: 0.9, maxWidth: "100%" }}>
              {compactCountdown(s, today)}
            </span>
          </a>
        ))
      ) : (
        onHostPage ? (
          // On the Host Antara page: straight to its own form.
          <a
            href={`#${HOST_FORM_ANCHOR}`}
            className="flex-1 btn-primary"
            style={{ padding: "12px 16px", fontSize: 12 }}
            data-testid="mobile-host-enquire-btn"
          >
            <PartyPopper size={14} /> Tell us about your occasion
          </a>
        ) : (
          <>
            <Link
              to={ROUTES.host.path}
              className="flex-1 btn-primary"
              style={{ padding: "12px 14px", fontSize: 12 }}
              data-testid="mobile-host-btn"
            >
              <PartyPopper size={14} /> Host Antara
            </Link>
            <a
              href={INTEREST_HREF}
              onClick={trackNotifyClick}
              className="btn-outline"
              style={{ padding: "12px 14px", fontSize: 12, whiteSpace: "nowrap" }}
              aria-label="Tell us your city"
              data-testid="mobile-interest-btn"
            >
              <Bell size={14} /> Your city
            </a>
          </>
        )
      )}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackWhatsAppClick}
        className="btn-outline"
        style={{ padding: "12px 14px", fontSize: 12 }}
        aria-label="A question? WhatsApp"
        data-testid="mobile-whatsapp-btn"
      >
        <MessageCircle size={14} />
      </a>
    </div>
  );
}
